import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Flex, Link, TableColumn, Text} from '@priority-software/priority-style-react';
import texts from '../../../texts';
import styles from './NumerationTemplatesStep.module.scss';
import {InputWithInnerState, Loader, Notification, ShowMoreText, Table} from '../../../Components';
import {
    getBranchesForm,
    getNumerationTemplatesForm,
    runNumerationTemplatesProc
} from '../../../actions/numerationTemplates';
import classnames from 'classnames';
import {APIActionStatus, Column, Form, formsStore, Record} from '../../../../../stores/Form';
import {observer, useObserver} from 'mobx-react';
import {Dictionary, NextState} from '../../../types';
import {useFooterContext} from '../../../hooks';
import {RecordEditState} from '../../../../../stores/Form/enums';
import {startForm} from '../../../../../_shared/GWTBridge/invoke/netForm';
import {useFiscalYear} from '../../../hooks/useFiscalYear';

const columnNames = ['PATDES', 'PREFIX', 'SVALUE', 'WIDTH', 'FIRSTDOCNO'];
const editableColumnNames = ['PREFIX', 'SVALUE', 'WIDTH'];
const branchesCheckField = 'IDENT';

const columnWidthMapping: Dictionary<number> = {
    PREFIX: 100,
    SVALUE: 150,
    WIDTH: 120,
    FIRSTDOCNO: 150
};

export const NumerationTemplatesStep: FC = observer(() => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [isProcCalled, setIsProcCalled] = useState(false);
    const [branchesInitialized, setBranchesInitialized] = useState(false);
    const [showBranchNotice, setShowBranchNotice] = useState(false);
    const [loadingNumerationTemplates, setLoadingNumerationTemplates] = useState<boolean>(false);
    const tableWrapperRef = useRef<HTMLDivElement>();
    const footerState = useFooterContext();
    const [fiscalYearStateLoaded, fiscalYearState] = useFiscalYear();
    const [numerationTemplatesForm, setNumerationTemplatesForm] = useState<Form | null>(null);

    const columnLocalizationMapping: Dictionary = useMemo(
        () => ({
            PATDES: texts['column_description'],
            PREFIX: texts['column_prefix'],
            SVALUE: texts['column_initial_no'],
            WIDTH: texts['column_length'],
            FIRSTDOCNO: texts['column_preview']
        }),
        [texts]
    );

    useEffect(() => {
        if (branchesInitialized && !isInitialized) {
            setLoadingNumerationTemplates(true);
            getNumerationTemplatesForm(fiscalYearState?.year.toString()).then(form => {
                setNumerationTemplatesForm(form);
                if (form.records.filter(x => !x.isNew).length === 0) {
                    formsStore.deleteForm(numerationTemplatesForm?.session);
                }
                setIsInitialized(true);
                setLoadingNumerationTemplates(false);
            });
        }
    }, [branchesInitialized, isInitialized]);

    useEffect(() => {
        if (fiscalYearStateLoaded) {
            setBranchesInitialized(false);
            footerState.setIsValid(false);
            getBranchesForm().then(form => {
                const columnId = form.columns.find(x => x.Name === branchesCheckField)!.id;
                const records = form.records
                    .filter(x => !x.isNew)
                    .map(x => x.fields.find(y => y.columnId === columnId));
                setShowBranchNotice(records.some(x => x.origValue === ''));
                formsStore.deleteForm(form.session);
                setBranchesInitialized(true);
            });
        }
    }, [fiscalYearStateLoaded]);

    useEffect(() => {
        if (footerState.onNextState === NextState.Processing) {
            formsStore.deleteForm(numerationTemplatesForm.session);
            footerState.setOnNextState(NextState.Done);
        }
    }, [footerState.onNextState]);

    const openNumericTemplates = () => {
        setLoadingNumerationTemplates(true);
        formsStore.deleteForm(numerationTemplatesForm.session);
        runNumerationTemplatesProc(fiscalYearState?.year.toString())
            .then(() => {
                getNumerationTemplatesForm(fiscalYearState?.year.toString()).then(form => {
                    setNumerationTemplatesForm(form);
                    setLoadingNumerationTemplates(false);
                    footerState.setIsValid(true);
                });
            })
            .catch(() => {
                setLoadingNumerationTemplates(false);
                footerState.setIsValid(true);
            })
            .finally(() => {
                setIsProcCalled(true);
            });
    };

    const numericTemplatesForm: Form | null = useObserver(() =>
        branchesInitialized ? numerationTemplatesForm : null
    );

    const numericTemplatesRecords = useObserver(
        () => numericTemplatesForm?.records.filter(x => !x.isNew) || []
    );

    useEffect(() => {
        footerState.setIsValid(
            isProcCalled ? true : !loadingNumerationTemplates && numericTemplatesRecords.length > 0
        );
    }, [loadingNumerationTemplates, isProcCalled]);

    const tableHeight = useCallback(
        () => tableWrapperRef.current?.clientHeight,
        [tableWrapperRef.current?.clientHeight, showBranchNotice, numericTemplatesForm]
    );

    const onFormInputBlur = async (e: FocusEvent, row: Record, column: Column) => {
        const target = e.target as HTMLInputElement;

        if (
            [RecordEditState.edit, RecordEditState.error].includes(
                formsStore.getScreenBySession(numericTemplatesForm.session).editMode
            )
        ) {
            if (
                row.recordIndex === numericTemplatesForm.currentRow &&
                column.id === numericTemplatesForm.currentColumnId
            ) {
                setLoadingNumerationTemplates(true);
                await formsStore
                    .getScreenBySession(numericTemplatesForm.session)
                    .saveRecord()
                    .then(status => {
                        if (status === APIActionStatus.blocked) {
                            target.focus();
                        }
                        setLoadingNumerationTemplates(false);
                    });
            } else {
                return;
            }
        }
        if (row.getField(column.id).currentValue === row.getField(column.id).origValue) {
            return;
        }
        setLoadingNumerationTemplates(true);
        numericTemplatesForm.setCurrentRow(row.recordIndex);
        numericTemplatesForm.setCurrentColumnId(column.id);
        formsStore
            .getScreenBySession(numericTemplatesForm.session)
            .fieldUpdate(numericTemplatesForm.activeField())
            .then(status => {
                if (status === APIActionStatus.passed) {
                    formsStore
                        .getScreenBySession(numericTemplatesForm.session)
                        .saveRecord()
                        .then(status => {
                            if (status === APIActionStatus.blocked) {
                                target.focus();
                            }
                            setLoadingNumerationTemplates(false);
                        });
                } else {
                    target.focus();
                    numericTemplatesForm.currentRecord
                        .getField(column.id as number)
                        .setCurrentValue(
                            numericTemplatesForm.currentRecord.getField(column.id as number)
                                .origValue as string
                        );
                    setLoadingNumerationTemplates(false);
                }
            })
            .catch(e => {
                setLoadingNumerationTemplates(false);
            });
    };

    const tableColumns: TableColumn[] = useObserver(() =>
        numericTemplatesForm
            ? [
                  ...numericTemplatesForm.columns
                      .filter(column => columnNames.includes(column.Name))
                      .map(column => {
                          return {
                              key: column.Title,
                              title: columnLocalizationMapping[column.Name],
                              dataIndex: column.Title,
                              width: columnWidthMapping[column.Name],
                              render: (_, row: Record) => {
                                  return !editableColumnNames.includes(column.Name) ? (
                                      <Text tag={'span'} skin={'paragraph-2'}>
                                          {row.getField(column.id).currentValue}
                                      </Text>
                                  ) : (
                                      <InputWithInnerState
                                          value={row.getField(column.id).currentValue as string}
                                          onChange={event => {
                                              row.getField(column.id).setCurrentValue(
                                                  event.target.value
                                              );
                                          }}
                                          onBlur={(e: FocusEvent) =>
                                              onFormInputBlur(e, row, column)
                                          }
                                      />
                                  );
                              }
                          } as TableColumn;
                      })
              ]
            : []
    );

    const onBranchFormClick = () => {
        startForm('BRANCHES');
    };

    return (
        <Flex
            componentClasses={classnames(styles.wrapper, {
                [styles.wrapperFullHeight]: !!numericTemplatesForm
            })}
            direction={'column'}
        >
            {showBranchNotice && (
                <Notification
                    message={
                        <>
                            {texts['stage0_step2_branches_notice']}
                            <br />
                            {texts['stage0_step2_branches_notice_continue']}
                            <Link
                                componentClasses={styles.notificationLink}
                                onClick={() => onBranchFormClick()}
                            >
                                <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-500'}>
                                    {texts['stage0_step2_branches_notice_link']}
                                </Text>
                            </Link>
                            {texts['stage0_step2_branches_notice_ending']}
                        </>
                    }
                />
            )}
            <ShowMoreText
                text={texts['stage0_step2_explanation']}
                extendedText={texts['stage0_step2_more_text']}
            />
            {!(numericTemplatesForm && numericTemplatesRecords.length > 0) && (
                <Flex componentClasses={styles.buttons}>
                    <Button
                        disabled={loadingNumerationTemplates || !branchesInitialized}
                        onClick={() => openNumericTemplates()}
                        wideButton
                        size={'medium'}
                    >
                        {texts['open_numeric_templates']}
                    </Button>
                </Flex>
            )}
            <div ref={tableWrapperRef} className={styles.table}>
                {numericTemplatesForm &&
                    tableHeight !== undefined &&
                    numericTemplatesRecords.length > 0 && (
                        <Table
                            rowKey={obj => obj.recordIndex}
                            componentClasses={styles.table}
                            columns={tableColumns}
                            data={numericTemplatesRecords}
                        />
                    )}
            </div>
            <Loader
                componentClassname={classnames(styles.loader, {
                    [styles.loaderWithNotice]: showBranchNotice
                })}
                isVisible={loadingNumerationTemplates}
            />
        </Flex>
    );
});
